'use client';

/**
 * Reveal — entrance animation wrapper (Figma 81:70 §Motion).
 *
 * Inspirado em nortecognitivo.com.br: blocos sobem 24px + fade-in quando
 * entram no viewport. Respeita prefers-reduced-motion (motion lib trata
 * auto via reducedMotion="user").
 */
import * as React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';

export interface RevealProps {
  children: React.ReactNode;
  /** Atraso em segundos antes da animação começar. Default 0. */
  delay?: number;
  /** Deslocamento Y inicial em pixels. Default 24. */
  y?: number;
  /** Duração da transição em segundos. Default 0.7. */
  duration?: number;
  /** Tag HTML do wrapper. Default 'div'. */
  as?: 'div' | 'section' | 'article' | 'header' | 'footer' | 'main' | 'aside' | 'li' | 'ul';
  className?: string;
  /** Margem do IntersectionObserver. Default '-80px' (revela um pouco antes). */
  rootMargin?: string;
  /** Se true, anima uma vez e mantém. Default true. */
  once?: boolean;
}

export function Reveal({
  children,
  delay = 0,
  y = 24,
  duration = 0.7,
  as = 'div',
  className,
  rootMargin = '-80px',
  once = true,
}: RevealProps) {
  const prefersReducedMotion = useReducedMotion();

  const variants: Variants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : y,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay,
        duration,
        ease: 'easeOut' as const,
      },
    },
  };

  const MotionTag = motion[as] as React.ComponentType<
    React.ComponentProps<typeof motion.div>
  >;

  return (
    <MotionTag
      initial="hidden"
      whileInView="visible"
      viewport={{ once, margin: rootMargin }}
      variants={variants}
      className={className}
    >
      {children}
    </MotionTag>
  );
}
